import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Minimize2, Plus, Lightbulb, RefreshCw, Save, Download, CheckCircle, Loader } from 'lucide-react'
import { engineApiClient } from '@/lib/apiClient'
import type { OutputDrafts } from '@/lib/contracts'
import { exportSectionToWord, exportAllToWord } from '@/lib/exportToWord'

const FALLBACK_PROJECT_ID = 'alpha-grid'

const SECTIONS: { key: keyof OutputDrafts; label: string }[] = [
  { key: 'jd', label: 'Job Description' },
  { key: 'brief', label: 'Search Brief' },
  { key: 'interviewGuide', label: 'Interview Guide' },
  { key: 'scorecard', label: 'Scorecard' },
  { key: 'summary', label: 'Executive Summary' },
  { key: 'outreach', label: 'Outreach' },
]

export const OutputEditor = () => {
  const { id } = useParams<{ id: string }>()
  const projectId = id ?? FALLBACK_PROJECT_ID
  const navigate = useNavigate()

  const [drafts, setDrafts] = useState<OutputDrafts | null>(null)
  const [activeSection, setActiveSection] = useState<keyof OutputDrafts>('jd')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const [saved, setSaved] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isExportingAll, setIsExportingAll] = useState(false)

  useEffect(() => {
    setIsLoading(true)
    engineApiClient
      .getOutputs(projectId)
      .then((res) => setDrafts({ ...res.drafts }))
      .catch(() => setError('Unable to load outputs. Please try again.'))
      .finally(() => setIsLoading(false))
  }, [projectId, retryCount])

  const OUTPUT_TYPE_MAP: Record<keyof OutputDrafts, string> = {
    jd: 'jd',
    brief: 'search-brief',
    interviewGuide: 'interview-guide',
    scorecard: 'scorecard',
    summary: 'summary',
    outreach: 'outreach',
  }

  const handleChange = (value: string) => {
    if (!drafts) return
    setDrafts((prev) => prev ? { ...prev, [activeSection]: value } : prev)
    setSaved(false)
  }

  const handleSave = async () => {
    if (!drafts) return
    setIsSaving(true)
    try {
      await engineApiClient.updateOutput(projectId, OUTPUT_TYPE_MAP[activeSection], drafts[activeSection])
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch {
      // fail silently — don't break editor
    } finally {
      setIsSaving(false)
    }
  }

  const handleExportAll = async () => {
    setIsExportingAll(true)
    try {
      const res = await engineApiClient.getOutputs(projectId)
      const sections = [
        { label: 'Full JD', content: res.drafts.jd },
        { label: 'Search Brief', content: res.drafts.brief },
        { label: 'Interview Guide', content: res.drafts.interviewGuide },
        { label: 'Scorecard', content: res.drafts.scorecard },
        { label: 'Executive Summary', content: res.drafts.summary },
        { label: 'Outreach Brief', content: res.drafts.outreach },
      ]
      exportAllToWord(sections)
    } catch {
      // silent
    } finally {
      setIsExportingAll(false)
    }
  }

  const applyTool = (fn: (text: string) => string) => {
    if (!drafts) return
    setDrafts((prev) => prev ? { ...prev, [activeSection]: fn(prev[activeSection]) } : prev)
    setSaved(false)
  }

  const currentText = drafts?.[activeSection] ?? ''
  const activeLabel = SECTIONS.find(s => s.key === activeSection)?.label ?? ''

  return (
    <div className="bg-[#02182B] min-h-screen flex flex-col">
      {/* Page header */}
      <div className="border-b border-[#233D4C]">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Output Editor</h1>
            <p className="text-gray-400 text-sm mt-0.5">Edit and refine your generated deliverables</p>
          </div>
          <button
            onClick={() => navigate(`/project/${projectId}/output`)}
            className="text-gray-400 hover:text-[#FD802E] text-sm transition-colors"
          >
            ← Back to Outputs
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-24 gap-2 text-gray-400">
          <Loader size={20} className="animate-spin text-[#FD802E]" />
          <span>Loading outputs…</span>
        </div>
      )}

      {error && !isLoading && (
        <div className="max-w-md mx-auto mt-16 text-center space-y-4">
          <p className="text-gray-400 text-sm">{error}</p>
          <button
            onClick={() => { setError(null); setRetryCount(c => c + 1) }}
            className="bg-[#233D4C] border border-[#2a4a5c] text-white text-sm px-4 py-2 rounded-lg hover:border-[#FD802E]/40 transition-colors"
          >
            Try again
          </button>
        </div>
      )}

      {drafts && !isLoading && (
        <div className="flex-1 flex flex-col lg:flex-row max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 gap-6">

          {/* Left — Section nav */}
          <div className="hidden lg:block w-52 flex-shrink-0">
            <p className="text-xs uppercase text-gray-400 font-medium mb-3">Sections</p>
            <nav className="space-y-1">
              {SECTIONS.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setActiveSection(s.key)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    activeSection === s.key
                      ? 'bg-[#FD802E]/10 text-[#FD802E] font-medium'
                      : 'text-gray-400 hover:text-white hover:bg-[#233D4C]'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Center — Editor */}
          <div className="flex-1 min-w-0 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-white font-semibold text-lg">{activeLabel}</h2>
              {saved && (
                <span className="text-green-400 text-xs flex items-center gap-1">
                  <CheckCircle size={12} /> Saved
                </span>
              )}
            </div>
            <textarea
              value={currentText}
              onChange={(e) => handleChange(e.target.value)}
              className="w-full min-h-[540px] bg-[#02182B] border border-[#2a4a5c] rounded-lg px-5 py-4 text-white text-sm font-mono leading-relaxed focus:outline-none focus:border-[#FD802E] transition-colors resize-none"
            />
          </div>

          {/* Right — Tools */}
          <div className="w-full lg:w-56 lg:flex-shrink-0 space-y-4">
            {/* Editing tools */}
            <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-4 space-y-2">
              <p className="text-xs uppercase text-gray-400 font-medium mb-3">Editing Tools</p>
              <button
                onClick={() => applyTool(t => t.replace(/\bshould\b/g, 'must').replace(/\bmight\b/g, 'will'))}
                className="w-full flex items-center gap-2 bg-[#02182B] border border-[#2a4a5c] text-white text-sm px-3 py-2 rounded-lg hover:border-[#FD802E]/40 transition-colors"
              >
                <RefreshCw size={13} /> Tighten language
              </button>
              <button
                onClick={() => applyTool(t => {
                  const lines = t.split('\n').filter(Boolean)
                  return lines.slice(0, Math.ceil(lines.length * 0.7)).join('\n')
                })}
                className="w-full flex items-center gap-2 bg-[#02182B] border border-[#2a4a5c] text-white text-sm px-3 py-2 rounded-lg hover:border-[#FD802E]/40 transition-colors"
              >
                <Minimize2 size={13} /> Trim length
              </button>
              <button
                onClick={() => applyTool(t => t)}
                className="w-full flex items-center gap-2 bg-[#02182B] border border-[#2a4a5c] text-white text-sm px-3 py-2 rounded-lg hover:border-[#FD802E]/40 transition-colors"
              >
                <Plus size={13} /> Add specificity
              </button>
              <button
                onClick={() => applyTool(t => t)}
                className="w-full flex items-center gap-2 bg-[#02182B] border border-[#2a4a5c] text-white text-sm px-3 py-2 rounded-lg hover:border-[#FD802E]/40 transition-colors"
              >
                <Lightbulb size={13} /> Improve clarity
              </button>
            </div>

            {/* Actions */}
            <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-4 space-y-2">
              <p className="text-xs uppercase text-gray-400 font-medium mb-3">Actions</p>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="w-full flex items-center gap-2 bg-[#FD802E] text-[#233D4C] font-semibold text-sm px-3 py-2 rounded-lg hover:bg-[#ff8f45] transition-colors disabled:opacity-50"
              >
                {isSaving ? <Loader size={13} className="animate-spin" /> : <Save size={13} />}
                {isSaving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => {
                  if (drafts) {
                    const label = SECTIONS.find(s => s.key === activeSection)?.label ?? String(activeSection)
                    exportSectionToWord(label, drafts[activeSection])
                  }
                }}
                className="w-full flex items-center gap-2 bg-[#02182B] border border-[#2a4a5c] text-white text-sm px-3 py-2 rounded-lg hover:border-[#FD802E]/40 transition-colors"
              >
                <Download size={13} /> Export to Word
              </button>
              <button
                onClick={handleExportAll}
                disabled={isExportingAll}
                className="w-full flex items-center gap-2 bg-[#02182B] border border-[#2a4a5c] text-white text-sm px-3 py-2 rounded-lg hover:border-[#FD802E]/40 transition-colors disabled:opacity-50"
              >
                <Download size={13} /> {isExportingAll ? 'Exporting…' : 'Export All'}
              </button>
              <button
                onClick={() => navigate(`/project/${projectId}/dashboard`)}
                className="w-full flex items-center gap-2 bg-[#02182B] border border-[#2a4a5c] text-white text-sm px-3 py-2 rounded-lg hover:border-[#FD802E]/40 transition-colors"
              >
                <CheckCircle size={13} /> Approve Final
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
